import {
  FINANCIAL_CATEGORIES,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES,
} from './constants';
import { getAmountCents } from './money';

export const BUDGET_SCHEMA_VERSION = 1;
export const BUDGET_WARNING_PERCENTAGE = 80;

export const BUDGET_PROGRESS_STATE = Object.freeze({
  EMPTY: 'empty',
  NORMAL: 'normal',
  WARNING: 'warning',
  REACHED: 'reached',
  EXCEEDED: 'exceeded',
});

export const isValidBudgetLimitCents = (value) =>
  Number.isSafeInteger(value) && value >= 0;

export const isValidBudgetCategory = (category, allowedCategories = FINANCIAL_CATEGORIES) =>
  typeof category === 'string' && allowedCategories.includes(category);

export const normalizeCategoryLimits = (limits = {}, allowedCategories = FINANCIAL_CATEGORIES) =>
  Object.fromEntries(
    Object.entries(limits).filter(
      ([category, value]) =>
        isValidBudgetCategory(category, allowedCategories) && isValidBudgetLimitCents(value)
    )
  );

export const getBudgetProgress = (limitCents, usedCents) => {
  const safeLimit = isValidBudgetLimitCents(limitCents) ? limitCents : 0;
  const safeUsed = isValidBudgetLimitCents(usedCents) ? usedCents : 0;
  const percentage = safeLimit > 0
    ? Math.round((safeUsed / safeLimit) * 100)
    : 0;

  let state = BUDGET_PROGRESS_STATE.EMPTY;
  if (safeLimit > 0 && safeUsed > safeLimit) {
    state = BUDGET_PROGRESS_STATE.EXCEEDED;
  } else if (safeLimit > 0 && safeUsed === safeLimit) {
    state = BUDGET_PROGRESS_STATE.REACHED;
  } else if (safeLimit > 0 && percentage >= BUDGET_WARNING_PERCENTAGE) {
    state = BUDGET_PROGRESS_STATE.WARNING;
  } else if (safeLimit > 0) {
    state = BUDGET_PROGRESS_STATE.NORMAL;
  }

  return {
    limitCents: safeLimit,
    usedCents: safeUsed,
    remainingCents: safeLimit - safeUsed,
    percentage,
    progressPercentage: Math.min(100, Math.max(0, percentage)),
    state,
  };
};

const emptyCategoryUsage = () => ({
  realizedExpenseCents: 0,
  committedExpenseCents: 0,
});

export const calculateBudgetUsage = ({ transactions = [], budget = null, validCategories = FINANCIAL_CATEGORIES } = {}) => {
  const totals = {
    realizedExpenseCents: 0,
    committedExpenseCents: 0,
  };
  const byCategory = new Map();

  transactions.forEach((transaction) => {
    if (transaction?.tipo !== TRANSACTION_TYPES.EXPENSE) return;

    const amountCents = getAmountCents(transaction);
    if (amountCents === null) return;

    const isPaid = transaction.status === TRANSACTION_STATUS.PAID;
    const isPending = transaction.status === TRANSACTION_STATUS.PENDING;
    if (!isPaid && !isPending) return;

    totals.committedExpenseCents += amountCents;
    if (isPaid) totals.realizedExpenseCents += amountCents;

    const category = transaction.categoria || 'Outros';
    const categoryUsage = byCategory.get(category) || emptyCategoryUsage();
    categoryUsage.committedExpenseCents += amountCents;
    if (isPaid) categoryUsage.realizedExpenseCents += amountCents;
    byCategory.set(category, categoryUsage);
  });

  const categoryLimits = normalizeCategoryLimits(budget?.categoryLimits, validCategories);
  const categories = Object.entries(categoryLimits)
    .map(([category, limitCents]) => {
      const usage = byCategory.get(category) || emptyCategoryUsage();
      return {
        category,
        ...usage,
        ...getBudgetProgress(limitCents, usage.committedExpenseCents),
      };
    })
    .sort((left, right) => right.percentage - left.percentage || left.category.localeCompare(right.category, 'pt-BR'));

  return {
    ...totals,
    pendingExpenseCents:
      totals.committedExpenseCents - totals.realizedExpenseCents,
    monthly: getBudgetProgress(
      budget?.monthlyLimitCents,
      totals.committedExpenseCents
    ),
    categories,
  };
};

export const deriveBudgetInsights = (usage) => {
  if (!usage) return [];
  const insights = [];
  const exceeded = usage.categories.find(
    (category) => category.state === BUDGET_PROGRESS_STATE.EXCEEDED
  );
  const closest = usage.categories
    .filter((category) => category.limitCents > 0)
    .sort((left, right) => right.percentage - left.percentage)[0];
  const mostConsumed = [...usage.categories]
    .sort((left, right) => right.committedExpenseCents - left.committedExpenseCents)[0];

  if (usage.monthly.state === BUDGET_PROGRESS_STATE.EXCEEDED) {
    insights.push({ type: 'exceeded', category: null, amountCents: Math.abs(usage.monthly.remainingCents) });
  }
  if (exceeded) {
    insights.push({ type: 'category-exceeded', category: exceeded.category, percentage: exceeded.percentage });
  } else if (closest) {
    insights.push({ type: 'closest-category', category: closest.category, percentage: closest.percentage });
  }
  if (mostConsumed) {
    insights.push({ type: 'most-consumed', category: mostConsumed.category, amountCents: mostConsumed.committedExpenseCents });
  }
  if (usage.pendingExpenseCents > 0) {
    insights.push({ type: 'pending', amountCents: usage.pendingExpenseCents });
  }

  return insights;
};

export const projectRecurringCostCents = (amountCents, occurrences) => {
  if (!isValidBudgetLimitCents(amountCents) || !Number.isSafeInteger(occurrences) || occurrences < 0) {
    return null;
  }
  const total = amountCents * occurrences;
  return Number.isSafeInteger(total) ? total : null;
};
